import { Controller } from "stimulus"

// Champ fichier / photo avec retour visuel quand l'utilisateur remplace le document existant.
//
// Cibles (toutes optionnelles) :
//   preview   <img> de l'apercu (photo actuelle ou avatar par defaut)
//   fileName  <span> qui affiche le nom du fichier
//   sizeError message d'erreur « taille > 2Mo »
//
// Quand un nouveau fichier est choisi :
//   - l'apercu est remplace par le nouveau fichier (images) ;
//   - le nom du fichier est mis a jour ;
//   - un bandeau « Nouveau fichier » + bouton « Annuler » est affiche (cree ici, aucun HTML requis) ;
//   - l'element recoit la classe `is-changed` ;
//   - « Annuler » restaure l'etat d'origine (apercu, nom, input vide).
const MAX_SIZE = 2000000;

export default class extends Controller {
  static targets = [ 'preview', 'fileName', 'sizeError' ];

  connect() {
    this.input = this.element.querySelector('input[type="file"]');
    if (!this.input) { return; }

    this.objectUrl = null;
    this.originalSrc = this.hasPreviewTarget ? this.previewTarget.getAttribute('src') : null;
    this.originalName = this.hasFileNameTarget ? this.fileNameTarget.textContent : '';

    this.onChange = this.fileChange.bind(this);
    this.onStart = this.uploadStart.bind(this);
    this.onProgress = this.uploadProgress.bind(this);
    this.onError = this.uploadError.bind(this);
    this.onEnd = this.uploadEnd.bind(this);

    this.input.addEventListener('change', this.onChange);
    this.element.addEventListener('direct-upload:start', this.onStart);
    this.element.addEventListener('direct-upload:progress', this.onProgress);
    this.element.addEventListener('direct-upload:error', this.onError);
    this.element.addEventListener('direct-upload:end', this.onEnd);
  }

  disconnect() {
    if (!this.input) { return; }

    this.input.removeEventListener('change', this.onChange);
    this.element.removeEventListener('direct-upload:start', this.onStart);
    this.element.removeEventListener('direct-upload:progress', this.onProgress);
    this.element.removeEventListener('direct-upload:error', this.onError);
    this.element.removeEventListener('direct-upload:end', this.onEnd);
    this._revokeUrl();
  }

  // ── Envoi direct ──────────────────────────────────────────────────────────
  uploadStart() {
    this._setName('Téléchargement...');
    if (this.hasFileNameTarget) { this.fileNameTarget.classList.add('uploading'); }
    this._setStatusText('Téléchargement...');
  }

  uploadProgress(e) {
    const text = `Téléchargement... ${parseInt(e.detail.progress, 10)}%`;
    this._setName(text);
    this._setStatusText(text);
  }

  uploadError() {
    if (this.hasFileNameTarget) { this.fileNameTarget.classList.remove('uploading'); }
    alert('Erreur durant le téléchargement');
  }

  uploadEnd(e) {
    const file = e.target.files && e.target.files[0];
    if (file) {
      this._setName(file.name);
      this._setStatusText(file.name);
    }
  }

  // ── Choix d'un fichier ────────────────────────────────────────────────────
  fileChange(e) {
    const file = e.target.files[0];

    // Selection annulee dans la fenetre systeme : on revient a l'etat d'origine.
    if (!file) { this.reset(); return; }

    if (file.size > MAX_SIZE) {
      if (this.hasSizeErrorTarget) { this.sizeErrorTarget.classList.remove('d-none'); }
      this.reset();
      return;
    }

    if (this.hasSizeErrorTarget) { this.sizeErrorTarget.classList.add('d-none'); }

    this._setName(file.name);
    this._showPreview(file);
    this._showStatus(file.name);
    this.element.classList.add('is-changed');
  }

  // Annule le nouveau fichier et restaure l'ancien affichage.
  reset(event) {
    if (event) { event.preventDefault(); }

    try { this.input.value = ''; } catch (ex) { /* navigateurs anciens */ }

    this._revokeUrl();
    this._setName(this.originalName);

    if (this.hasPreviewTarget) {
      if (this.originalSrc) { this.previewTarget.setAttribute('src', this.originalSrc); }
      this.previewTarget.classList.remove('is-hidden');
    }

    if (this.statusElement) { this.statusElement.classList.add('d-none'); }
    this.element.classList.remove('is-changed');
  }

  // ── Interne ───────────────────────────────────────────────────────────────
  _showPreview(file) {
    if (!this.hasPreviewTarget) { return; }

    this._revokeUrl();

    // Un PDF ne peut pas s'afficher dans une <img> : on masque l'ancien apercu,
    // le bandeau « Nouveau fichier » indique le nom du document choisi.
    if (!file.type.startsWith('image/')) {
      this.previewTarget.classList.add('is-hidden');
      return;
    }

    this.previewTarget.classList.remove('is-hidden');
    this.objectUrl = URL.createObjectURL(file);
    this.previewTarget.setAttribute('src', this.objectUrl);
  }

  _showStatus(name) {
    if (!this.statusElement) { this._buildStatus(); }

    this.statusElement.classList.remove('d-none');
    this._setStatusText(name);
  }

  _buildStatus() {
    const el = document.createElement('div');
    el.className = 'ng-upload-status d-none';
    el.innerHTML =
      '<span class="tag is-success is-light">Nouveau fichier</span>' +
      '<span class="ng-upload-status-name"></span>' +
      '<button type="button" class="button is-small is-text ng-upload-status-cancel">Annuler</button>';

    el.querySelector('button').addEventListener('click', this.reset.bind(this));

    // Place le bandeau juste avant le message d'erreur de taille s'il existe, sinon a la fin.
    if (this.hasSizeErrorTarget) {
      this.sizeErrorTarget.parentNode.insertBefore(el, this.sizeErrorTarget);
    } else {
      this.element.appendChild(el);
    }

    this.statusElement = el;
  }

  _setStatusText(text) {
    if (!this.statusElement) { return; }

    this.statusElement.querySelector('.ng-upload-status-name').textContent = text;
  }

  _setName(text) {
    if (this.hasFileNameTarget) { this.fileNameTarget.textContent = text; }
  }

  _revokeUrl() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }
}
